/**
 * Logout-on-401: the single rule that *any* 401 from an internal API
 * call logs the user out (design §1.2–§1.4).
 *
 * Every network call funnels through ``apiFetch`` (which normalises a
 * non-2xx into an ``ApiError`` carrying ``.status``) and every caller
 * consumes it through a React Query hook, so the ``QueryClient``'s
 * ``QueryCache``/``MutationCache`` ``onError`` callbacks are the one
 * place that observes every failed query and mutation. ``query-client.ts``
 * wires ``handleQueryError`` / ``handleMutationError`` there; this module
 * holds the decision logic so it stays unit-testable without a
 * ``QueryClient``.
 *
 * Reaction differs by call kind because the data at stake differs:
 *   - **Query 401** → immediate hard navigation to ``/signin`` (a query
 *     is a background read with nothing to protect; a full document load
 *     also discards all in-memory authed state on logout).
 *   - **Mutation 401** → a soft "you've been signed out" prompt with a
 *     **Sign in** action, leaving the form mounted so the user keeps
 *     their input (PM decision §6.1).
 *
 * A first-time visitor who never had a session must not be told "you've
 * been signed out"; the ``sessionWasEstablished`` flag (set on the first
 * ``/api/auth/me/`` success via ``markSessionEstablished``) gates the
 * ``session_expired`` notice, so a cold-load 401 is a plain gate to
 * sign-in. A ``handling`` latch collapses a same-tick fan-out of 401s
 * into a single reaction — one redirect or one toast, never a stack
 * (§6.5).
 */

import { ApiError } from "@/lib/api/client"
import { isPublicRoute, sanitizeNextPath } from "@/lib/auth/public-routes"
import { showSessionExpiredToast } from "@/components/session-expired-toast"

/**
 * Build the ``/signin`` target for an unauthorized call, or ``null`` when
 * the current route is public (a 401 there is expected — e.g. the
 * anonymous ``/api/auth/me/`` probe on ``/signin`` — and must not bounce
 * the visitor).
 *
 * ``next`` is the current location run through the ``sanitizeNextPath``
 * open-redirect guard so ``/signin`` can return the user where they were;
 * if it fails the guard it is simply omitted. ``auth_error=session_expired``
 * is appended only when ``expired`` is true (a genuine mid-session
 * expiry), so a first-time visitor sees a clean sign-in screen (§1.3.1).
 *
 * Pure: no ``window``, no React, no side effects — unit-testable in
 * isolation.
 */
export function signinRedirectForUnauthorized(
  pathname: string,
  search: string,
  { expired }: { expired: boolean },
): string | null {
  if (isPublicRoute(pathname)) {
    return null
  }
  const normalizedSearch = search.startsWith("?") ? search.slice(1) : search
  const currentPath = normalizedSearch
    ? `${pathname}?${normalizedSearch}`
    : pathname
  const safeNext = sanitizeNextPath(currentPath)

  const parts: string[] = []
  if (safeNext) {
    parts.push(`next=${encodeURIComponent(safeNext)}`)
  }
  if (expired) {
    parts.push("auth_error=session_expired")
  }
  return parts.length > 0 ? `/signin?${parts.join("&")}` : "/signin"
}

// ---------------------------------------------------------------------------
// Impure cache handlers + their effects.
//
// The effects (hard navigate / soft prompt) are indirected through a
// swappable ``effects`` object so the handler branching is testable —
// jsdom's ``window.location`` is immutable and cannot be stubbed, so a
// test injects fakes via ``setSessionExpiryEffectsForTests`` instead.
// ---------------------------------------------------------------------------

export interface SessionExpiryEffects {
  /** Query path: hard, state-wiping navigation to the sign-in target. */
  navigate: (target: string) => void
  /** Mutation path: non-blocking re-auth prompt with a Sign in action. */
  promptReauth: (target: string) => void
}

const defaultEffects: SessionExpiryEffects = {
  navigate: (target) => {
    window.location.replace(target)
  },
  promptReauth: (target) => {
    showSessionExpiredToast(target)
  },
}

/**
 * Key on a mutation's ``meta`` that opts it out of the 401 re-auth prompt.
 * Use ``suppressSessionExpiryMeta`` as the ``meta`` value. The explicit
 * **Sign out** mutation sets this: its ``POST /api/auth/logout/`` may 401
 * when the session is already dead, and the global handler must not flash
 * a misleading "you've been signed out — your changes are still here"
 * prompt over a deliberate logout (the mutation's own ``onSettled``
 * already routes to ``/signin``).
 */
const SUPPRESS_META_KEY = "suppressAuthExpiryHandler"

/** Ready-made ``meta`` value that exempts a mutation from the 401 prompt. */
export const suppressSessionExpiryMeta = { [SUPPRESS_META_KEY]: true } as const

/**
 * Which reaction has fired this tick. The query path (hard redirect)
 * supersedes the mutation path (soft prompt): a query 401 wins regardless
 * of arrival order, while same-kind 401s are deduped to a single reaction
 * (§6.5). Cleared on the next macrotask so a later, independent 401 can
 * still react; the query path also hard-navigates, wiping module state.
 */
type Reaction = "none" | "redirecting" | "prompted"

let effects: SessionExpiryEffects = defaultEffects
let sessionWasEstablished = false
let reaction: Reaction = "none"

/**
 * Record that a real session existed (called on the first
 * ``/api/auth/me/`` success). Flips later 401s from "first-time gate" to
 * "mid-session expiry", which gets the ``session_expired`` notice / soft
 * prompt.
 *
 * Lifetime: this flag persists for the JS context and is only cleared by a
 * full document load (which the query redirect path always triggers). The
 * mutation prompt does not reload, so a future soft re-login flow must
 * reset it explicitly to avoid a stale ``session_expired`` notice.
 */
export function markSessionEstablished(): void {
  sessionWasEstablished = true
}

function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401
}

function currentRedirectTarget(): string | null {
  return signinRedirectForUnauthorized(
    window.location.pathname,
    window.location.search,
    { expired: sessionWasEstablished },
  )
}

function scheduleReactionReset(): void {
  setTimeout(() => {
    reaction = "none"
  }, 0)
}

/**
 * ``QueryCache.onError`` handler — query 401 → immediate hard redirect.
 *
 * A query is a background read with nothing to protect, so it logs out at
 * once and supersedes any soft prompt already raised this tick (the hard
 * navigation discards the page anyway). Only an in-flight redirect dedupes
 * a fan-out of query 401s.
 */
export function handleQueryError(error: unknown): void {
  if (!isUnauthorized(error)) {
    return
  }
  if (reaction === "redirecting") {
    return
  }
  const target = currentRedirectTarget()
  if (target === null) {
    return
  }
  reaction = "redirecting"
  scheduleReactionReset()
  effects.navigate(target)
}

/**
 * ``MutationCache.onError`` handler — mutation 401 → soft re-auth prompt.
 *
 * A mutation is the user acting (e.g. clicking Save), so it must not
 * discard their input. It is suppressed when any reaction is already in
 * flight — an in-progress query redirect wins, and a prompt is never
 * stacked. ``meta`` carrying ``suppressAuthExpiryHandler`` opts the
 * mutation out entirely (see ``SUPPRESS_META_KEY``).
 */
export function handleMutationError(
  error: unknown,
  meta?: Record<string, unknown>,
): void {
  if (meta?.[SUPPRESS_META_KEY] === true) {
    return
  }
  if (!isUnauthorized(error)) {
    return
  }
  if (reaction !== "none") {
    return
  }
  const target = currentRedirectTarget()
  if (target === null) {
    return
  }
  reaction = "prompted"
  scheduleReactionReset()
  effects.promptReauth(target)
}

/**
 * Test seam: override the navigate / prompt effects (jsdom's
 * ``window.location`` is immutable). Pass ``null`` to restore defaults.
 */
export function setSessionExpiryEffectsForTests(
  next: Partial<SessionExpiryEffects> | null,
): void {
  effects = next ? { ...defaultEffects, ...next } : defaultEffects
}

/** Test seam: clear the reaction latch and the session-established flag. */
export function resetSessionExpiryStateForTests(): void {
  sessionWasEstablished = false
  reaction = "none"
}
